"use client";

/**
 * Pinned drift gallery (Venetian recipe 5).
 *
 * The section pins for ~3000px of scroll while its absolutely positioned
 * children (portrait images, floating testimonial cards) travel from
 * +100vh to -100vh, ease none, scrubbed, staggered i * 0.12. An optional
 * giant low-opacity serif "S" watermark (the icon.svg motif in Fraunces)
 * sits behind, scaling 1 to 1.75 and fading to 0.5 over the same scrub.
 * Desktop pointers get a mousemove parallax of plus/minus 25px
 * (duration 0.5, power2.out) on each child's inner element.
 *
 * Usage: give the section a height (e.g. className="h-screen") and pass
 * absolutely positioned children; each child should wrap its visual in
 * one inner element if you want the mouse parallax (the drift transform
 * lives on the child, the parallax on its first inner element).
 *
 * Reduced motion: no pin, no drift; children render wherever the caller
 * positioned them. Content never depends on the animation.
 */
import { useRef, type HTMLAttributes } from "react";
import { gsap, useGSAP, prefersReducedMotion } from "./gsap";

export interface PinnedDriftProps extends HTMLAttributes<HTMLElement> {
  /** Scroll distance the section stays pinned for, in px. Default 3000. */
  pinDistance?: number;
  /** Render the giant serif S watermark behind the drifting children. */
  watermark?: boolean;
  /**
   * Optional image src for the watermark. When set, the image replaces the
   * serif "S" (e.g. the stitched Silk monogram). Blended over the surface.
   */
  watermarkSrc?: string;
  /**
   * Where the drift starts, as a fraction of the viewport height below the
   * fold. 1 = a full viewport below (long empty lead-in); smaller values
   * bring the first cards into view sooner. Default 1.
   */
  startOffset?: number;
  /** Per-child stagger for the drift, in timeline units. Default 0.12. */
  stagger?: number;
}

export function PinnedDrift({
  children,
  pinDistance = 3000,
  watermark = false,
  watermarkSrc,
  startOffset = 1,
  stagger = 0.12,
  className = "",
  ...rest
}: PinnedDriftProps) {
  const ref = useRef<HTMLElement>(null);
  const layerRef = useRef<HTMLDivElement>(null);
  const markRef = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      const el = ref.current;
      if (!el || prefersReducedMotion()) return;
      // Children marked data-drift-static stay put while the stage is
      // pinned (e.g. a copy block beside the drifting cards).
      const items = (
        Array.from(layerRef.current?.children ?? []) as HTMLElement[]
      ).filter((c) => !c.hasAttribute("data-drift-static"));

      const mm = gsap.matchMedia();

      // Pin + drift are desktop-only: below 1024px the absolutely-scattered
      // field cannot fit a narrow viewport, so the section falls back to the
      // static flowing column (same as no-JS / reduced motion) and stays
      // legible. matchMedia reverts the tweens if the viewport crosses the
      // breakpoint.
      mm.add("(min-width: 1024px)", () => {
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: el,
            start: "top top",
            end: `+=${pinDistance}`,
            pin: true,
            scrub: true,
            anticipatePin: 1,
            invalidateOnRefresh: true,
          },
        });

        if (items.length) {
          tl.fromTo(
            items,
            { y: () => window.innerHeight * startOffset },
            { y: () => -window.innerHeight, ease: "none", stagger },
            0
          );
        }

        if (markRef.current) {
          tl.fromTo(
            markRef.current,
            { scale: 1, opacity: 1 },
            { scale: 1.75, opacity: 0.5, ease: "none" },
            0
          );
        }
      });

      // Desktop-only mouse parallax on each child's inner element. The
      // scrub owns `y` on the child itself, so the parallax targets the
      // first inner element (falling back to x-only on the child).
      mm.add("(min-width: 1024px) and (hover: hover)", () => {
        const setters = items.map((item) => {
          const inner = item.firstElementChild as HTMLElement | null;
          const target = inner ?? item;
          return {
            x: gsap.quickTo(target, "x", {
              duration: 0.5,
              ease: "power2.out",
            }),
            y: inner
              ? gsap.quickTo(inner, "y", { duration: 0.5, ease: "power2.out" })
              : null,
          };
        });
        const onMove = (event: MouseEvent) => {
          const nx = (event.clientX / window.innerWidth) * 2 - 1;
          const ny = (event.clientY / window.innerHeight) * 2 - 1;
          setters.forEach((setter, i) => {
            // Alternate direction so the field drifts apart, not in a block.
            const dir = i % 2 === 0 ? 1 : -1;
            setter.x(nx * 25 * dir);
            setter.y?.(ny * 25 * dir);
          });
        };
        el.addEventListener("mousemove", onMove);
        return () => el.removeEventListener("mousemove", onMove);
      });
    },
    {
      scope: ref,
      dependencies: [pinDistance, startOffset, stagger],
      revertOnUpdate: true,
    }
  );

  return (
    <section
      ref={ref}
      className={`relative overflow-hidden ${className}`}
      {...rest}
    >
      {watermark && (
        <div
          ref={markRef}
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 flex select-none items-center justify-center"
        >
          {watermarkSrc ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={watermarkSrc}
              alt=""
              className="h-auto w-auto select-none"
              style={{
                width: "min(70vh, 62vw)",
                opacity: 0.5,
                mixBlendMode: "multiply",
              }}
            />
          ) : (
            <span
              className="font-serif italic leading-none text-ink"
              style={{ fontSize: "min(85vh, 70vw)", opacity: 0.06 }}
            >
              S
            </span>
          )}
        </div>
      )}
      <div ref={layerRef} className="relative h-full w-full">
        {children}
      </div>
    </section>
  );
}
